import { AnyAction } from "redux";
import { AuthActionTypes } from "redux/actions/actionTypes";
import {
  EmailBuilderActionType,
  isEmailBuilderAction,
  type TEmailBuilderAction,
} from "redux/actions/emailBuilder/emailBuilderAction";
import type { TEmailBuilderAddInsertContext } from "pages/secure/facility/marketing/emailBuilder/emailBuilderAddInsertContextTypes";
import type { EmailDocument } from "pages/secure/facility/marketing/emailBuilder/domain/types";
import type { TEmailBuilderWorkspace } from "pages/secure/facility/marketing/emailBuilder/emailBuilderWorkspace";
import type {
  IEmailCampaignDesignStatus,
  TEmailBuilderReferencedMediaMap,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import { enrichEmailDocumentFromLoadContext } from "pages/secure/facility/marketing/emailBuilder/domain/enrich-document-from-load-context";
import { clearEmailBuilderLibraryMediaSources } from "pages/secure/facility/marketing/emailBuilder/domain/media";
import { repairEmailDocumentUnavailableLibraryMedia } from "pages/secure/facility/marketing/emailBuilder/operations/repairEmailDocumentUnavailableLibraryMedia";
import { emailCampaignDesignHasUnpublishedChanges } from "pages/secure/facility/marketing/emailCampaigns/emailCampaignDesignStatus";
import { cloneEmailDocumentBaseline, emailDocumentsEqual } from "pages/secure/facility/marketing/emailBuilder/emailBuilderAutosaveBaseline";

export type TEmailBuilderLoadStatus = "standby" | "loading" | "failed" | "succeeded";

export type TEmailBuilderAutoSaveStatus = "unsaved-changes" | "saving" | "saved" | "error";

export type TEmailBuilderActivePanel =
  | "add-elements"
  | "colors"
  | "templates"
  | "none";

export type TEmailBuilderEditingMode = "none" | "inline-content" | "block-settings";

export type TEmailBuilderMode = "design" | "preview";

export type TEmailBuilderPreviewViewport = "desktop" | "mobile";

export type TEmailBuilderPreviewStatus = "idle" | "loading" | "succeeded" | "failed";

export interface IEmailBuilderCampaignMeta {
  id: number;
  name: string;
  content?: string | null;
}

export interface IEmailBuilderDraftMeta {
  id: number;
  campaignId: number;
  schemaVersion: number;
  revision: number;
  updatedAt: string | null;
}

export interface IEmailBuilderTemplateActivation {
  activeSourceDraftRevision: number;
  activatedAt: string | null;
  activeDocument: EmailDocument | null;
}

export interface IEmailBuilderState {
  loadStatus: TEmailBuilderLoadStatus;
  loadError: string | null;
  loadRequestId: string | null;
  loadRequestCampaignId: number | null;
  workspace: TEmailBuilderWorkspace | null;
  campaign: IEmailBuilderCampaignMeta | null;
  draft: IEmailBuilderDraftMeta | null;
  document: EmailDocument | null;
  editingMode: TEmailBuilderEditingMode;
  activePanel: TEmailBuilderActivePanel;
  addInsertContext: TEmailBuilderAddInsertContext | null;
  addPanelSuppressCloseOnSelectionChange: boolean;
  mode: TEmailBuilderMode;
  autoSaveStatus: TEmailBuilderAutoSaveStatus;
  autoSaveError: string | null;
  autoSaveConflict: boolean;
  autoSaveDueAt: number | null;
  autosaveBaselineDocument: EmailDocument | null;
  previewViewport: TEmailBuilderPreviewViewport;
  previewStatus: TEmailBuilderPreviewStatus;
  previewHtml: string | null;
  previewError: string | null;
  previewRevision: number | null;
  previewRequestId: string | null;
  previewRequestCampaignId: number | null;
  campaignDesign: IEmailCampaignDesignStatus | null;
  templateActivation: IEmailBuilderTemplateActivation | null;
  unavailableLibraryMediaRepairedCount: number;
}

export const emailBuilderInitialState: IEmailBuilderState = {
  loadStatus: "standby",
  loadError: null,
  loadRequestId: null,
  loadRequestCampaignId: null,
  workspace: null,
  campaign: null,
  draft: null,
  document: null,
  editingMode: "none",
  activePanel: "none",
  addInsertContext: null,
  addPanelSuppressCloseOnSelectionChange: false,
  mode: "design",
  autoSaveStatus: "saved",
  autoSaveError: null,
  autoSaveConflict: false,
  autoSaveDueAt: null,
  autosaveBaselineDocument: null,
  previewViewport: "desktop",
  previewStatus: "idle",
  previewHtml: null,
  previewError: null,
  previewRevision: null,
  previewRequestId: null,
  previewRequestCampaignId: null,
  campaignDesign: null,
  templateActivation: null,
  unavailableLibraryMediaRepairedCount: 0,
};

function prepareEmailBuilderLoadedDocument(
  document: EmailDocument,
  referencedMedia: TEmailBuilderReferencedMediaMap,
): {
  document: EmailDocument;
  autosaveBaselineDocument: EmailDocument;
  unavailableLibraryMediaRepairedCount: number;
} {
  const enriched = enrichEmailDocumentFromLoadContext(document, { referencedMedia });
  const repaired = repairEmailDocumentUnavailableLibraryMedia(enriched, referencedMedia);

  return {
    document: repaired.document,
    autosaveBaselineDocument:
      repaired.repairedCount > 0 ? enriched : repaired.document,
    unavailableLibraryMediaRepairedCount: repaired.repairedCount,
  };
}

export function isCurrentEmailBuilderRequest(
  activeRequestId: string | null,
  activeCampaignId: number | null,
  requestId: string | undefined,
  campaignId: number | undefined,
): boolean {
  if (activeRequestId === null || activeCampaignId === null) {
    return requestId === undefined;
  }

  return requestId === activeRequestId && campaignId === activeCampaignId;
}

function clearedPreviewState(): Pick<
  IEmailBuilderState,
  | "previewStatus"
  | "previewHtml"
  | "previewError"
  | "previewRevision"
  | "previewRequestId"
  | "previewRequestCampaignId"
> {
  return {
    previewStatus: "idle",
    previewHtml: null,
    previewError: null,
    previewRevision: null,
    previewRequestId: null,
    previewRequestCampaignId: null,
  };
}

function emailBuilderStateWithOpenBlockSettings(
  state: IEmailBuilderState,
): IEmailBuilderState | null {
  if (
    state.document === null ||
    state.loadStatus !== "succeeded" ||
    state.mode !== "design"
  ) {
    return null;
  }

  return {
    ...state,
    editingMode: "block-settings",
  };
}

function withSyncedDraftRevision(
  state: IEmailBuilderState,
  revision: number,
  updatedAt: string | null,
): IEmailBuilderState {
  if (state.draft === null) {
    return state;
  }

  const nextState: IEmailBuilderState = {
    ...state,
    draft: {
      ...state.draft,
      revision,
      updatedAt,
    },
  };

  if (state.campaignDesign === null || state.workspace?.kind !== "campaign") {
    return nextState;
  }

  const campaignDesign: IEmailCampaignDesignStatus = {
    ...state.campaignDesign,
    has_draft: true,
    draft_revision: revision,
    has_unpublished_changes: false,
  };

  return {
    ...nextState,
    campaignDesign: {
      ...campaignDesign,
      has_unpublished_changes: emailCampaignDesignHasUnpublishedChanges(campaignDesign, revision),
    },
  };
}

function emailBuilderReducer(state: IEmailBuilderState, action: TEmailBuilderAction): IEmailBuilderState {
  switch (action.type) {
    case EmailBuilderActionType.START_LOADING:
      clearEmailBuilderLibraryMediaSources();
      return {
        ...emailBuilderInitialState,
        loadStatus: "loading",
        loadRequestId: action.payload.requestId,
        loadRequestCampaignId: action.payload.campaignId,
        workspace:
          action.payload.workspaceKind === undefined
            ? null
            : { kind: action.payload.workspaceKind, id: action.payload.campaignId },
      };
    case EmailBuilderActionType.LOAD_SUCCEEDED: {
      if (
        !isCurrentEmailBuilderRequest(
          state.loadRequestId,
          state.loadRequestCampaignId,
          action.payload.requestId,
          action.payload.campaignId,
        )
      ) {
        return state;
      }

      const openAddForEmptyBody = false;
      const referencedMedia = action.payload.referencedMedia ?? {};
      const loadedDocument = prepareEmailBuilderLoadedDocument(
        action.payload.document,
        referencedMedia,
      );
      const document = loadedDocument.document;
      const templateActivation = action.payload.templateActivation ?? null;

      return {
        loadStatus: action.payload.loadStatus,
        loadError: null,
        loadRequestId: null,
        loadRequestCampaignId: null,
        workspace: action.payload.workspace,
        campaign: action.payload.campaign,
        draft: action.payload.draft,
        document,
        editingMode: "none",
        activePanel: openAddForEmptyBody ? "add-elements" : "none",
        addInsertContext: openAddForEmptyBody ? { type: "body" } : null,
        addPanelSuppressCloseOnSelectionChange: false,
        mode: "design",
        autoSaveStatus: "saved",
        autoSaveError: null,
        autoSaveConflict: false,
        autoSaveDueAt: null,
        autosaveBaselineDocument: cloneEmailDocumentBaseline(
          loadedDocument.autosaveBaselineDocument,
        ),
        previewViewport: "desktop",
        campaignDesign: action.payload.campaignDesign ?? null,
        unavailableLibraryMediaRepairedCount:
          loadedDocument.unavailableLibraryMediaRepairedCount,
        templateActivation:
          templateActivation === null
            ? null
            : {
                ...templateActivation,
                activeDocument:
                  templateActivation.activeDocument === null
                    ? null
                    : prepareEmailBuilderLoadedDocument(
                        templateActivation.activeDocument,
                        referencedMedia,
                      ).document,
              },
        ...clearedPreviewState(),
      };
    }
    case EmailBuilderActionType.LOAD_FAILED:
      if (
        !isCurrentEmailBuilderRequest(
          state.loadRequestId,
          state.loadRequestCampaignId,
          action.payload.requestId,
          action.payload.campaignId,
        )
      ) {
        return state;
      }

      return {
        ...emailBuilderInitialState,
        loadStatus: action.payload.loadStatus,
        loadError: action.payload.loadError,
      };
    case EmailBuilderActionType.EXIT:
      clearEmailBuilderLibraryMediaSources();
      return { ...emailBuilderInitialState };
    case EmailBuilderActionType.OPEN_INLINE_CONTENT:
      if (state.mode !== "design") {
        return state;
      }

      return {
        ...state,
        editingMode: "inline-content",
      };
    case EmailBuilderActionType.CLOSE_INLINE_CONTENT:
      return {
        ...state,
        editingMode: state.editingMode === "inline-content" ? "none" : state.editingMode,
      };
    case EmailBuilderActionType.OPEN_BLOCK_SETTINGS: {
      const openedState = emailBuilderStateWithOpenBlockSettings(state);
      return openedState ?? state;
    }
    case EmailBuilderActionType.ENTER_BLOCK_SETTINGS_MODE:
      if (state.mode !== "design") {
        return state;
      }
      return {
        ...state,
        editingMode: "block-settings",
      };
    case EmailBuilderActionType.CLOSE_BLOCK_SETTINGS:
      return {
        ...state,
        editingMode: "none",
      };
    case EmailBuilderActionType.SYNC_EDITOR_DOCUMENT: {
      if (state.document === null || state.loadStatus !== "succeeded") {
        return state;
      }

      if (emailDocumentsEqual(state.document, action.payload.document)) {
        return state;
      }

      return {
        ...state,
        document: cloneEmailDocumentBaseline(action.payload.document),
      };
    }
    case EmailBuilderActionType.CONSUME_ADD_PANEL_INSERT_SUPPRESS:
      if (!state.addPanelSuppressCloseOnSelectionChange) {
        return state;
      }

      return {
        ...state,
        addPanelSuppressCloseOnSelectionChange: false,
      };
    case EmailBuilderActionType.UPDATE_AUTOSAVE_STATUS:
      return {
        ...state,
        autoSaveStatus: action.payload.autoSaveStatus,
        autoSaveError: action.payload.autoSaveError,
        autoSaveConflict: action.payload.autoSaveConflict,
        autoSaveDueAt: action.payload.autoSaveDueAt,
      };
    case EmailBuilderActionType.AUTOSAVE_SUCCEEDED: {
      const next = withSyncedDraftRevision(state, action.payload.revision, action.payload.updatedAt);
      return {
        ...next,
        autoSaveStatus: "saved",
        autoSaveError: null,
        autoSaveConflict: false,
        autoSaveDueAt: null,
        autosaveBaselineDocument: cloneEmailDocumentBaseline(action.payload.baselineDocument),
      };
    }
    case EmailBuilderActionType.SYNC_DRAFT_REVISION:
      return withSyncedDraftRevision(state, action.payload.revision, action.payload.updatedAt);
    case EmailBuilderActionType.TEMPLATE_ACTIVATED:
      if (state.workspace?.kind !== "template") {
        return state;
      }

      return {
        ...state,
        templateActivation: {
          activeSourceDraftRevision: action.payload.activeSourceDraftRevision,
          activatedAt: action.payload.activatedAt,
          activeDocument:
            state.document !== null ? cloneEmailDocumentBaseline(state.document) : null,
        },
      };
    case EmailBuilderActionType.CANCEL_PENDING_AUTOSAVE:
      if (state.autoSaveStatus !== "unsaved-changes" && state.autoSaveStatus !== "saving") {
        return state;
      }

      return {
        ...state,
        autoSaveStatus: "saved",
        autoSaveDueAt: null,
        autoSaveError: null,
      };
    case EmailBuilderActionType.SET_ACTIVE_PANEL:
      if (action.payload.activePanel === "add-elements") {
        return {
          ...state,
          activePanel: "add-elements",
          addInsertContext: action.payload.addInsertContext ?? null,
        };
      }

      return {
        ...state,
        activePanel: action.payload.activePanel,
        addInsertContext: null,
        addPanelSuppressCloseOnSelectionChange: false,
      };
    case EmailBuilderActionType.SET_MODE: {
      if (action.payload.mode === "preview") {
        return {
          ...state,
          mode: "preview",
          editingMode: "none",
        };
      }

      return {
        ...state,
        mode: "design",
        ...clearedPreviewState(),
      };
    }
    case EmailBuilderActionType.SET_PREVIEW_VIEWPORT:
      return {
        ...state,
        previewViewport: action.payload.previewViewport,
      };
    case EmailBuilderActionType.SET_CAMPAIGN_NAME:
      if (state.campaign === null) {
        return state;
      }

      return {
        ...state,
        campaign: {
          ...state.campaign,
          name: action.payload.name,
        },
      };
    case EmailBuilderActionType.PREVIEW_START:
      return {
        ...state,
        previewStatus: "loading",
        previewHtml: null,
        previewError: null,
        previewRevision: null,
        previewRequestId: action.payload.requestId,
        previewRequestCampaignId: action.payload.campaignId,
      };
    case EmailBuilderActionType.PREVIEW_SUCCEEDED:
      if (
        state.mode !== "preview" ||
        !isCurrentEmailBuilderRequest(
          state.previewRequestId,
          state.previewRequestCampaignId,
          action.payload.requestId,
          action.payload.campaignId,
        )
      ) {
        return state;
      }

      return {
        ...state,
        previewStatus: "succeeded",
        previewHtml: action.payload.previewHtml,
        previewError: null,
        previewRevision: action.payload.previewRevision,
        previewRequestId: null,
        previewRequestCampaignId: null,
      };
    case EmailBuilderActionType.PREVIEW_FAILED:
      if (
        state.mode !== "preview" ||
        !isCurrentEmailBuilderRequest(
          state.previewRequestId,
          state.previewRequestCampaignId,
          action.payload.requestId,
          action.payload.campaignId,
        )
      ) {
        return state;
      }

      return {
        ...state,
        previewStatus: "failed",
        previewHtml: null,
        previewError: action.payload.previewError,
        previewRevision: null,
        previewRequestId: null,
        previewRequestCampaignId: null,
      };
    default:
      return state;
  }
}

export function EmailBuilderReducer(
  state: IEmailBuilderState = emailBuilderInitialState,
  action: AnyAction,
): IEmailBuilderState {
  if (action.type === AuthActionTypes.USER_LOGIN || action.type === AuthActionTypes.USER_LOGOUT) {
    return { ...emailBuilderInitialState };
  }

  if (isEmailBuilderAction(action)) {
    return emailBuilderReducer(state, action);
  }

  return state;
}
